import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copyright, Scales, ShieldCheck, Handshake, Warning } from "@phosphor-icons/react";
import { BrandMark } from "@/components/Brand";
import useSeo from "@/lib/useSeo";

const POINTS = [
  {
    icon: Scales,
    title: "Trademarks",
    body: "ARISSAI® is a registered trademark of ARISSAI in Canada (CIPO). The ARISSAI wordmark and the ARISSAI & Design logo (the blue \"AI\" lens-mark) are registered marks of ARISSAI. All rights in the marks are reserved. Unauthorised use of the name, wordmark, or logo — including in product names, domains, marketing materials, or look-alike marks — is prohibited and may constitute trademark infringement under the Canadian Trademarks Act and applicable international laws.",
  },
  {
    icon: Copyright,
    title: "Copyright & original work",
    body: "© 2026 ARISSAI. All rights reserved. ARISSAI is an original invention. The six-lens framework (Assumption · Risk · Issue · Solution · Insights · Integration), the underlying AI-orchestrated reasoning workflows, the platform source code, user interface designs, written content, documentation, and analytical methodologies are the proprietary intellectual property of ARISSAI and are protected under applicable copyright laws. No part of the platform may be reproduced, reverse-engineered, scraped, redistributed, or commercially reused, in whole or in part, without prior written permission. Licensing enquiries are welcome.",
  },
  {
    icon: ShieldCheck,
    title: "Third-party trademarks · no affiliation",
    body: "ARISSAI is independently built and operated. We are not affiliated with, sponsored by, or endorsed by any third-party project-management vendor whose file formats we ingest. References to such file types or formats appear solely as descriptive compatibility statements (nominative fair use). All third-party product names, company names, and logos remain the property of their respective owners.",
  },
];

export default function Legal() {
  useSeo({
    title: "Trademarks & IP · ARISSAI® · Registered with CIPO Canada",
    description: "ARISSAI® is a registered trademark of ARISSAI with the Canadian Intellectual Property Office (CIPO). The ARISSAI wordmark and lens-mark logo are registered marks. Read the full trademark and IP policy.",
    path: "/legal",
  });
  return (
    <div className="min-h-screen bg-zinc-100" data-testid="legal-page">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <BrandMark size={32} tagline="AI-Driven Project Intelligence" />
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-zinc-950" data-testid="legal-back-link">
            <ArrowLeft size={14} weight="bold" /> Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-16">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020F5]">// Legal · Intellectual Property</div>
        <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3">Intellectual Property.</h1>
        <p className="mt-6 text-zinc-600 text-base leading-relaxed">
          ARISSAI is original work — a new invention in project intelligence. The brand, the platform, and the six-lens framework are protected under applicable trademark and copyright laws, and we take protecting them seriously.
        </p>

        <div className="mt-12 grid gap-6">
          {POINTS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="bg-white border border-zinc-200 p-6" data-testid={`legal-card-${p.title.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 40)}`}>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 grid place-items-center bg-zinc-950 text-white shrink-0">
                    <Icon size={18} weight="duotone" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-display font-bold text-lg text-zinc-950">{p.title}</h2>
                    <p className="text-sm text-zinc-600 mt-1.5 leading-relaxed">{p.body}</p>
                    {p.title === "Trademarks" && (
                      <a
                        href="mailto:hello@arissai.com?subject=ARISSAI%20infringement%20report"
                        className="mt-4 inline-flex items-center gap-2 h-9 px-4 bg-zinc-950 text-white text-[11px] font-bold tracking-[0.1em] uppercase hover:bg-[#0020F5]"
                        data-testid="legal-report-infringement"
                      >
                        <Warning size={12} weight="bold" /> Report infringement →
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Partnership CTA */}
        <div className="mt-12 bg-[#0020F5] text-white p-7" data-testid="legal-partnership-card">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 grid place-items-center bg-white text-[#0020F5] shrink-0">
              <Handshake size={18} weight="duotone" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/70">// Partnership</div>
              <h2 className="font-display font-bold text-xl mt-1">Build something with us.</h2>
              <p className="text-sm text-white/85 mt-2 leading-relaxed">
                We're open to consulting partnerships, integrations, and design-partner pilots with PMO teams and transformation consultancies. If you'd like to bring ARISSAI to your clients or co-build a vertical-specific lens, drop us a line.
              </p>
              <a
                href="mailto:hello@arissai.com?subject=ARISSAI%20partnership%20enquiry"
                className="mt-4 inline-flex items-center gap-2 h-10 px-5 bg-white text-[#0020F5] text-xs font-bold tracking-[0.1em] uppercase hover:bg-zinc-100"
                data-testid="legal-partnership-mailto"
              >
                <Handshake size={14} weight="bold" /> Partner with us →
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12 bg-zinc-950 text-white p-6">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-400">// Licensing & enquiries</div>
          <p className="mt-3 text-sm leading-relaxed">
            To license the ARISSAI methodology, report an infringement, or request brand-asset usage, contact <a href="mailto:hello@arissai.com?subject=ARISSAI%20licensing%20enquiry" className="text-white underline hover:text-[#9DA8FF]">hello@arissai.com</a>.
          </p>
        </div>

        <div className="mt-10 text-xs text-zinc-500 font-mono">ARISSAI® · © 2026 ARISSAI · All rights reserved.</div>
      </main>
    </div>
  );
}
